/**
 * HOF-696 D19 — the login phone field reads a number against a chosen country.
 *
 * Before this, every national number became +49 (`LoginPage.tsx`), so a Romanian
 * worker typing the number on their contract (`0721 345 678`) asked for an SMS to a
 * German number that does not exist. The country selector supplies the dial code;
 * an explicitly international number (`+…` or `00…`) always wins over it.
 *
 * One contract with the backend (codex r1 #6): the selector offers exactly the countries
 * `OTPRequest` accepts (`backend/src/schemas/auth.py::_PHONE_PATTERNS`), with the same
 * subscriber-digit ranges, and an international number from any other country is refused
 * here rather than 422'd there. Both lists are pinned to
 * `config/auth/login_phone_countries@v1.json` — by `__tests__/phone.test.ts` on this side and
 * `backend/tests/unit/schemas/test_login_phone_countries_hof696.py` on that one. Adding a
 * country is an edit to all three in one PR.
 */

export type PhoneCountryCode = 'DE' | 'RO' | 'PL' | 'AT' | 'FR' | 'NL';

export interface PhoneCountry {
  code: PhoneCountryCode;
  dial: string;
  /** The trunk prefix dialled before a national number, dropped in E.164. */
  trunk: string;
  /** Inclusive digit range after the dial code — the backend's pattern for this country. */
  subscriberDigits: readonly [number, number];
}

/** DE, RO and PL first — the farm's own and its workers' most common countries. */
export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  { code: 'DE', dial: '+49', trunk: '0', subscriberDigits: [10, 12] },
  { code: 'RO', dial: '+40', trunk: '0', subscriberDigits: [9, 9] },
  { code: 'PL', dial: '+48', trunk: '', subscriberDigits: [9, 9] },
  { code: 'AT', dial: '+43', trunk: '0', subscriberDigits: [10, 12] },
  { code: 'FR', dial: '+33', trunk: '0', subscriberDigits: [9, 10] },
  { code: 'NL', dial: '+31', trunk: '0', subscriberDigits: [9, 10] },
];

export type PhoneParseResult = { ok: true; e164: string } | { ok: false };

/** Valid only for a supported country, with that country's subscriber length. */
function finish(candidate: string): PhoneParseResult {
  if (!/^\+\d+$/.test(candidate)) return { ok: false };
  const country = PHONE_COUNTRIES.find((c) => candidate.startsWith(c.dial));
  if (!country) return { ok: false };
  const digits = candidate.length - country.dial.length;
  const [low, high] = country.subscriberDigits;
  return digits >= low && digits <= high ? { ok: true, e164: candidate } : { ok: false };
}

export function parsePhoneToE164(input: string, countryCode: PhoneCountryCode): PhoneParseResult {
  const country = PHONE_COUNTRIES.find((c) => c.code === countryCode) ?? PHONE_COUNTRIES[0];
  // Spaces, dashes, dots, slashes and brackets are how numbers are printed.
  const compact = input.trim().replace(/[\s\-./()]/g, '');
  if (compact === '') return { ok: false };

  if (compact.startsWith('+')) return finish(compact);
  if (!/^\d+$/.test(compact)) return { ok: false };
  if (compact.startsWith('00')) return finish(`+${compact.slice(2)}`);

  const dialDigits = country.dial.slice(1);
  // The country code typed without `+` (`40721…` with RO selected). Only for the
  // selected country, and only when the rest is long enough to be a subscriber
  // number — a short national number that happens to start with those digits
  // stays national.
  if (compact.startsWith(dialDigits) && compact.length - dialDigits.length >= 9) {
    return finish(`+${compact}`);
  }

  const national = country.trunk && compact.startsWith(country.trunk) ? compact.slice(country.trunk.length) : compact;
  return finish(`${country.dial}${national}`);
}

/** The country the selector starts on: the worker's language, else Germany. */
export function defaultCountryForLanguage(language: string | null | undefined): PhoneCountryCode {
  const base = (language ?? '').toLowerCase().split('-')[0];
  if (base === 'ro') return 'RO';
  if (base === 'pl') return 'PL';
  return 'DE';
}
